<template>
  <div>
    <div class="dropzone__container">
      <vue-dropzone
        :id="id"
        ref="dropzone"
        :class="dropzoneClasses"
        :options="dropzoneOptions"
        duplicate-check
        @vdropzone-success="onSuccess"
        @vdropzone-error="onError"
        @vdropzone-removed-file="removedFile"
        @vdropzone-max-files-reached="onMaxFilesReached"
        @vdropzone-max-files-exceeded="onMaxFilesExceeded"
        @vdropzone-sending="onSending"
        @vdropzone-complete="onComplete"
      />
      <span
        :class="[
          'dropzone__size',
          $style['max-files'],
          { [$style['max-files--warn']]: maxFilesWarn },
        ]"
      >
        {{
          $tc('form.dropzone.maxFiles', dropzoneOptions.maxFiles, {
            maxFiles: dropzoneOptions.maxFiles,
          })
        }}
      </span>

      <span
        v-if="acceptedFilesShort.length"
        :class="[
          'dropzone__types',
          $style['accepted-files'],
          { [$style['accepted-files--warn']]: acceptedFilesWarn },
        ]"
      >
        {{
          $tc('form.dropzone.acceptedFiles', acceptedFilesShort.length, {
            acceptedFiles: acceptedFilesShort,
          })
        }}
      </span>
    </div>

    <div v-if="!hideDetails && errorMessages.length > 0" class="mt-1 v-text-field__details">
      <div class="v-messages theme--light error--text" role="alert">
        <div class="v-messages__wrapper">
          <div class="v-messages__message">{{ errorMessages[0] }}</div>
        </div>
      </div>
    </div>

    <BaseAlert v-if="serverErrorMessages.length > 0" type="error" class="mb-0 mt-4">
      <template #message>
        <div>
          <h3 class="body-1 font-weight-bold mb-1">{{ $t('form.dropzone.uploadFailed') }}</h3>

          <ul class="pa-0">
            <li
              v-for="({ statusCode, errorTranslated }, index) in serverErrorMessages"
              :key="`se_${index}`"
              class="v-messages__message pl-0 body-2"
              :class="{ 'mb-0': index === serverErrorMessages.length - 1 }"
            >
              <span v-if="errorTranslated"
                >{{ $t('form.dropzone.dictResponseError', { statusCode }) }}:
                <b>{{ errorTranslated }}</b></span
              >
              <span v-else>{{ $t('form.dropzone.dictResponseError', { statusCode }) }}</span>
            </li>
          </ul>
        </div>
      </template>
    </BaseAlert>
  </div>
</template>

<script>
import { uniqueId } from 'lodash-es'
// import * as vue2Dropzone from 'vue2-dropzone'
// ToDo: try to use package / use git from packages.json / really add sources and dependencies to our packages.json
import vue2Dropzone from '@/js/components/Shared/vue-dropzone/src/components/vue-dropzone.vue'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'

// Constants
export const FILE_DROPZONE_EVENTS = {
  VDROPZONE_MOUNTED: 'vdropzone-mounted',
  VDROPZONE_FILE_ADDED: 'vdropzone-file-added',
  VDROPZONE_FILES_ADDED: 'vdropzone-files-added',
  VDROPZONE_SUCCESS: 'vdropzone-success',
  VDROPZONE_ERROR: 'vdropzone-error',
  VDROPZONE_REMOVED_FILE: 'vdropzone-removed-file',
  VDROPZONE_SENDING: 'vdropzone-sending',
  VDROPZONE_SUCCESS_MULTIPLE: 'vdropzone-success-multiple',
  VDROPZONE_SENDING_MULTIPLE: 'vdropzone-sending-multiple',
  VDROPZONE_COMPLETE: 'vdropzone-complete',
  VDROPZONE_QUEUE_COMPLETE: 'vdropzone-queue-complete',
  VDROPZONE_TOTAL_UPLOAD_PROGRESS: 'vdropzone-total-upload-progress',
  VDROPZONE_DRAG_DROP: 'vdropzone-drag-drop',
  VDROPZONE_DRAG_START: 'vdropzone-drag-start',
  VDROPZONE_DRAG_END: 'vdropzone-drag-end',
  VDROPZONE_DRAG_ENTER: 'vdropzone-drag-enter',
  VDROPZONE_DRAG_OVER: 'vdropzone-drag-over',
  VDROPZONE_DRAG_LEAVE: 'vdropzone-drag-leave',
  VDROPZONE_DUPLICATE_FILE: 'vdropzone-duplicate-file',
}

// Defaults
export const FILE_DROPZONE_DEFAULT_OPTIONS = {
  maxFiles: 1,
  maxfilesexceeded: function (file) {
    this.removeFile(file)
  },
  addRemoveLinks: true,
  acceptedFiles:
    'image/bmp,image/x-bmp,image/x-ms-bmp,image/gif,image/jpeg,image/png,image/tiff,application/pdf',
  maxFilesize: 20, // default 20MB
}

export default {
  name: 'FileDropzone',
  components: {
    vueDropzone: vue2Dropzone,
  },
  props: {
    url: {
      type: String,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      id: uniqueId('file-dropzone_'),
      maxFilesWarn: false,
      acceptedFilesWarn: false,
      serverErrorMessages: [],
      dropzoneOptions: {
        url: this.url,
        ...FILE_DROPZONE_DEFAULT_OPTIONS,
        // Messages
        dictDefaultMessage: this.$t('form.dropzone.dictDefaultMessage'),
        dictFallbackMessage: this.$t('form.dropzone.dictFallbackMessage'),
        dictFallbackText: this.$t('form.dropzone.dictFallbackText'),
        dictFileTooBig: this.$t('form.dropzone.dictFileTooBig'),
        dictInvalidFileType: this.$t('form.dropzone.dictInvalidFileType'),
        dictResponseError: this.$t('form.dropzone.dictResponseError'),
        dictCancelUpload: this.$t('form.dropzone.dictCancelUpload'),
        dictCancelUploadConfirmation: this.$t('form.dropzone.dictCancelUploadConfirmation'),
        dictRemoveFile: this.$t('form.dropzone.dictRemoveFile'),
        dictMaxFilesExceeded: this.$t('form.dropzone.dictMaxFilesExceeded'),
        ...this.options,
      },
    }
  },
  computed: {
    acceptedFilesShort() {
      return this.dropzoneOptions.acceptedFiles
        .split(',')
        .map(type => `.${type.split('/').pop()}`)
        .filter(type => ['.x-bmp', '.x-ms-bmp'].indexOf(type) < 0)
        .join(', ')
    },
    dropzoneClasses() {
      return [
        this.$style.dropzone,
        {
          [this.$style['dropzone--single']]: !this.fullWidth && this.dropzoneOptions.maxFiles === 1,
        },
      ]
    },
  },
  watch: {
    value() {
      const files = this.getAcceptedFiles()
      if (this.value.length < files.length) {
        files.forEach(file => {
          if (!this.value.includes(file)) {
            this.removeFile(file)
          }
        })
        // files.forEach(file => {
        //   this.removeFile(file)
        // })
      }
    },
  },
  methods: {
    getAcceptedFiles() {
      return this.$refs.dropzone.getAcceptedFiles()
    },
    disable() {
      this.$refs.dropzone.disable()
    },
    removeFile(file) {
      this.$refs.dropzone.removeFile(file)
    },
    emitAcceptedFiles() {
      this.$emit('input', this.getAcceptedFiles())
    },
    onSending() {
      this.serverErrorMessages = []

      this.$emit(FILE_DROPZONE_EVENTS.VDROPZONE_SENDING)
    },
    onComplete() {
      this.$emit(FILE_DROPZONE_EVENTS.VDROPZONE_COMPLETE)
    },
    onSuccess(file, response) {
      if (this.dropzoneOptions.acceptedFiles.indexOf(file.type) > -1) {
        this.acceptedFilesWarn = false
      }

      this.emitAcceptedFiles()
      this.$emit(FILE_DROPZONE_EVENTS.VDROPZONE_SUCCESS, { file, response })
    },
    onError(file, errorMessage, xhr) {
      if (!file.accepted || file.status === 'error' || errorMessage) {
        this.removeFile(file)

        if (errorMessage) {
          const errorKey = `form.dropzone.responseError.${errorMessage?.message}`
          var errorTranslated = this.$t(errorKey)
          if (errorMessage?.print_message) {
            errorTranslated = errorMessage.message
          }
          this.serverErrorMessages.push({
            statusCode: xhr.status,
            errorMessage,
            errorTranslated: errorTranslated, // !== errorKey ? errorTranslated : null,
          })
        }

        if (this.dropzoneOptions.acceptedFiles.indexOf(file.type) < 0) {
          this.acceptedFilesWarn = true
        }
      }

      this.$emit(FILE_DROPZONE_EVENTS.VDROPZONE_ERROR, { file })
      this.emitAcceptedFiles()
    },
    onMaxFilesReached() {},
    onMaxFilesExceeded() {
      this.maxFilesWarn = true
    },
    removedFile() {
      if (this.getAcceptedFiles().length === 0) {
        this.maxFilesWarn = false
      }

      this.$emit(FILE_DROPZONE_EVENTS.VDROPZONE_REMOVED_FILE)
      this.emitAcceptedFiles()
    },
  },
}
</script>

<style lang="scss" module>
.accepted-files,
.max-files {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    font-size: var(--font-sm);
  }
  display: block;
  margin-top: 0.25rem;

  color: var(--grey);
  font-size: var(--font-xs);

  &--warn {
    color: var(--red);
  }
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 18.75rem;

  :global(.dz-preview) {
    z-index: 1;
  }

  &--single {
    display: inline-flex;
    width: 100%;
    height: 18.75rem;
  }
}
</style>
